import { create } from "zustand";
import type { AttachedFile, Message, ModelInfo } from "./api";
import type { Skill, SkillInput } from "./db/skills";

import { DEFAULT_AUTO_CONTEXT, type AutoContext } from "./context";
import { authHeaders } from "./auth";
import { apiFetch } from "./backend";
import { deleteLocalSandbox, pruneLocalSandboxes } from "./local-sandbox";

export type HomeTopic = { label: string; prompt: string; skillIds?: string[] };

export type ConversationSummary = {
  id: string;
  title: string;
  systemPrompt: string | null;
  folderId?: string | null;
  pinned?: boolean;
  createdAt: number;
  updatedAt: number;
};

export type Conversation = ConversationSummary & {
  messages: Message[];
  contextBlock: string | null;
  pinnedSkillId: string | null;
  activatedSkillIds: string[];
  workspacePath?: string | null;
};

export type WorkspacePrefs = {
  path: string;
  allowedPrefixes: string[];
  deniedPrefixes: string[];
  createdAt: number;
  updatedAt: number;
};

export type PendingApproval = {
  id: string;
  command: string;
  workspacePath: string;
  resolve: (decision: "once" | "always" | "deny") => void;
};

export type AppSettings = {
  defaultSystemPrompt: string;
  autoContext: AutoContext;
  activeModel?: string;
  thinkingEffort: "off" | "low" | "medium" | "high";
  homeTopics: HomeTopic[];
  toolPrefs: Record<string, boolean>;
  collapsedFolders: Record<string, boolean>;
};

const DEFAULT_SETTINGS: AppSettings = {
  defaultSystemPrompt: "",
  autoContext: DEFAULT_AUTO_CONTEXT,
  thinkingEffort: "medium",
  homeTopics: [],
  toolPrefs: {},
  collapsedFolders: {},
};

export type FolderSummary = {
  id: string;
  name: string;
  conversationCount: number;
  createdAt: number;
  updatedAt: number;
};

export type Draft = { text: string; files: AttachedFile[] };

export type ArtifactSummary = {
  id: string;
  name: string;
  tags: string[];
  fileCount: number;
  totalBytes: number;
  createdChatId: string | null;
  createdAt: number;
  updatedAt: number;
};

export type Artifact = ArtifactSummary & { files: Record<string, string> };

export type { Skill, SkillInput };

type State = {
  conversations: ConversationSummary[];
  folders: FolderSummary[];
  activeId: string | null;
  loadingChatId: string | null;
  activeRunChatIds: string[];
  messages: Message[];
  activeSystemPrompt: string | null;
  activeContextBlock: string | null;
  drafts: Record<string, Draft>;
  artifacts: ArtifactSummary[];
  artifactPanelOpen: boolean;
  artifactPanelSelectedId: string | null;
  sidebarOpen: boolean;
  showApiKey: boolean;
  skills: Skill[];
  activePinnedSkillId: string | null;
  activeActivatedSkillIds: string[];
  activeWorkspacePath: string | null;
  workspacePrefs: WorkspacePrefs | null;
  pendingApproval: PendingApproval | null;
  settings: AppSettings;
  models: ModelInfo[];
  activeModel: string;
  loaded: boolean;

  init(): Promise<void>;
  refreshConversations(): Promise<void>;
  refreshActiveRuns(): Promise<void>;
  refreshFolders(): Promise<void>;
  refreshModels(): Promise<void>;
  selectConversation(id: string): Promise<void>;
  startNewChat(): void;
  saveActive(messages: Message[]): Promise<string>;
  deleteConversation(id: string): Promise<void>;
  deleteAllConversations(): Promise<void>;
  setActiveTitle(id: string, title: string): Promise<void>;
  setActiveSystemPrompt(prompt: string | null): Promise<void>;
  setActiveContextBlock(block: string): Promise<void>;
  createFolder(name: string): Promise<void>;
  renameFolder(id: string, name: string): Promise<void>;
  deleteFolder(id: string): Promise<void>;
  moveChat(chatId: string, folderId: string | null): Promise<void>;
  togglePin(chatId: string, pinned: boolean): Promise<void>;
  updateSettings(patch: Partial<AppSettings>): Promise<void>;
  setActiveModel(model: string): void;
  setDraftText(key: string, text: string): void;
  setDraftFiles(key: string, files: AttachedFile[]): void;
  clearDraft(key: string): void;
  refreshArtifacts(): Promise<void>;
  deleteArtifact(id: string): Promise<void>;
  openArtifactPanel(id: string | null): void;
  closeArtifactPanel(): void;
  setSidebarOpen(open: boolean): void;
  setShowApiKey(show: boolean): void;
  refreshSkills(): Promise<void>;
  createSkill(input: SkillInput): Promise<Skill>;
  updateSkill(id: string, patch: Partial<SkillInput>): Promise<Skill>;
  deleteSkill(id: string): Promise<void>;
  setActivePinnedSkill(skillId: string | null): Promise<void>;
  setActiveActivatedSkills(skillIds: string[]): Promise<void>;
  setActiveWorkspace(path: string | null): Promise<void>;
  reloadWorkspacePrefs(): Promise<void>;
  saveWorkspacePrefs(patch: { allowedPrefixes?: string[]; deniedPrefixes?: string[] }): Promise<void>;
  requestApproval(command: string): Promise<"once" | "always" | "deny">;
  resolveApproval(decision: "once" | "always" | "deny"): void;
};

function generateId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function deriveTitle(content: string): string {
  let text = content
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/[#*_>]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const firstLine = text.split(/[.!?\n]/)[0]?.trim() ?? text;
  text = firstLine || text;
  if (!text) return "New chat";
  if (text.length <= 50) return text;
  const cut = text.slice(0, 50);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 20 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
}

function titleFromMessages(messages: Message[]): string {
  const first = messages.find((m) => m.role === "user");
  if (!first) return "New chat";
  return deriveTitle(first.content);
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await apiFetch(url, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}

async function patchConversation(id: string, body: Record<string, unknown>): Promise<void> {
  await api(`/api/conversations/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export const useStore = create<State>((set, get) => {
  const patchActive = async (stateUpdate: Partial<State>, body: Record<string, unknown>): Promise<void> => {
    set(stateUpdate as any);
    const { activeId } = get();
    if (activeId) await patchConversation(activeId, body);
  };

  return ({
  conversations: [],
  folders: [],
  activeId: null,
  loadingChatId: null,
  activeRunChatIds: [],
  messages: [],
  activeSystemPrompt: null,
  activeContextBlock: null,
  drafts: {},
  artifacts: [],
  artifactPanelOpen: false,
  artifactPanelSelectedId: null,
  sidebarOpen: typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches,
  showApiKey: false,
  skills: [],
  activePinnedSkillId: null,
  activeActivatedSkillIds: [],
  activeWorkspacePath: null,
  workspacePrefs: null,
  pendingApproval: null,
  settings: DEFAULT_SETTINGS,
  models: [],
  activeModel: "",
  loaded: false,

  async init() {
    const [convs, folders, settings, models] = await Promise.all([
      api<ConversationSummary[]>("/api/conversations"),
      api<FolderSummary[]>("/api/folders").catch(() => [] as FolderSummary[]),
      api<AppSettings>("/api/settings"),
      apiFetch("/api/v1/models", { headers: authHeaders() }).then((r) => r.json()).then((d) =>
        (d.data ?? []).map((m: any) => ({ id: m.id, contextLength: m.context_length ?? null })),
      ).catch(() => [] as ModelInfo[]),
    ]);

    const merged: AppSettings = {
      ...DEFAULT_SETTINGS,
      ...settings,
      autoContext: { ...DEFAULT_SETTINGS.autoContext, ...(settings?.autoContext ?? {}) },
      homeTopics: Array.isArray(settings?.homeTopics)
        ? settings.homeTopics.map((t: any) => {
            const ids = Array.isArray(t.skillIds)
              ? t.skillIds
              : t.skillId
              ? [t.skillId]
              : [];
            return { label: t.label, prompt: t.prompt, skillIds: ids };
          })
        : [],
      toolPrefs: settings?.toolPrefs && typeof settings.toolPrefs === "object" ? settings.toolPrefs : {},
      collapsedFolders: settings?.collapsedFolders && typeof settings.collapsedFolders === "object" ? settings.collapsedFolders : {},
    };

    set({
      conversations: convs,
      folders,
      settings: merged,
      models,
      activeModel: models.some((m) => m.id === merged.activeModel)
        ? merged.activeModel
        : models[0]?.id ?? "",
      loaded: true,
    });
    void get().refreshArtifacts();
    void get().refreshSkills();
    void pruneLocalSandboxes(convs.map((c) => c.id));
  },

  async refreshConversations() {
    const convs = await api<ConversationSummary[]>("/api/conversations");
    set({ conversations: convs });
  },

  async refreshActiveRuns() {
    try {
      const body = await api<{ runs: Array<{ chatId: string }> }>("/api/runs/active");
      const ids = Array.from(new Set((body.runs ?? []).map((r) => r.chatId)));
      const prev = get().activeRunChatIds;
      if (prev.length === ids.length && prev.every((id) => ids.includes(id))) return;
      set({ activeRunChatIds: ids });
    } catch {}
  },

  async refreshFolders() {
    const folders = await api<FolderSummary[]>("/api/folders");
    set({ folders });
  },

  async refreshModels() {
    try {
      const r = await apiFetch("/api/v1/models", { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json();
      const models: ModelInfo[] = (d.data ?? []).map((m: any) => ({
        id: m.id,
        contextLength: m.context_length ?? null,
      }));
      const { activeModel, settings } = get();
      const hasActive = models.some((m) => m.id === activeModel);
      const nextActive = hasActive ? activeModel : models[0]?.id ?? "";
      set({ models, activeModel: nextActive });
      if (nextActive !== activeModel) {
        const next = { ...settings, activeModel: nextActive };
        set({ settings: next });
        void api(`/api/settings`, { method: "PUT", body: JSON.stringify(next) });
      }
    } catch {}
  },

  async createFolder(name: string) {
    await api(`/api/folders`, { method: "POST", body: JSON.stringify({ name }) });
    await get().refreshFolders();
  },

  async renameFolder(id: string, name: string) {
    await api(`/api/folders/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });
    await get().refreshFolders();
  },

  async deleteFolder(id: string) {
    await api(`/api/folders/${id}`, { method: "DELETE" });
    await get().refreshFolders();
  },

  async moveChat(chatId: string, folderId: string | null) {
    await patchConversation(chatId, { folderId });
    await Promise.all([get().refreshConversations(), get().refreshFolders()]);
  },

  async togglePin(chatId: string, pinned: boolean) {
    set({ conversations: get().conversations.map((c) => (c.id === chatId ? { ...c, pinned } : c)) });
    await patchConversation(chatId, { pinned });
  },

  async selectConversation(id: string) {
    const prev = get();
    const sameChat = prev.activeId === id;
    set({ loadingChatId: id });
    try {
      const conv = await api<Conversation>(`/api/conversations/${id}`);
      if (get().loadingChatId !== id) return;
      set({
        activeId: id,
        loadingChatId: null,
        messages: conv.messages,
        activeSystemPrompt: conv.systemPrompt,
        activeContextBlock: conv.contextBlock ?? (sameChat ? prev.activeContextBlock : null),
        activePinnedSkillId: conv.pinnedSkillId ?? null,
        activeActivatedSkillIds: Array.isArray(conv.activatedSkillIds) ? conv.activatedSkillIds : [],
        activeWorkspacePath: conv.workspacePath ?? null,
        workspacePrefs: null,
      });
      if (conv.workspacePath) void get().reloadWorkspacePrefs();
    } catch (e) {
      if (get().loadingChatId === id) set({ loadingChatId: null });
      throw e;
    }
  },

  startNewChat() {
    set({
      activeId: null,
      loadingChatId: null,
      messages: [],
      activeSystemPrompt: null,
      activeContextBlock: null,
      activePinnedSkillId: null,
      activeActivatedSkillIds: [],
      activeWorkspacePath: null,
      workspacePrefs: null,
    });
  },

  async saveActive(messages: Message[]) {
    if (messages.length === 0) return get().activeId ?? "";
    const { activeId, activeSystemPrompt, conversations } = get();
    const id = activeId ?? generateId();
    const existing = conversations.find((c) => c.id === id);
    const now = Date.now();
    const conv: Conversation = {
      id,
      title: existing?.title ?? titleFromMessages(messages),
      systemPrompt: activeSystemPrompt,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      messages,
    };
    await api(`/api/conversations/${id}`, { method: "PUT", body: JSON.stringify(conv) });
    if (!activeId) set({ activeId: id });
    await get().refreshConversations();
    return id;
  },

  async deleteConversation(id: string) {
    await api(`/api/conversations/${id}`, { method: "DELETE" });
    void deleteLocalSandbox(id);
    const { drafts } = get();
    const nextDrafts = { ...drafts };
    delete nextDrafts[id];
    if (get().activeId === id) {
      set({ activeId: null, messages: [], activeSystemPrompt: null, activeContextBlock: null, drafts: nextDrafts });
    } else {
      set({ drafts: nextDrafts });
    }
    await get().refreshConversations();
  },

  async deleteAllConversations() {
    await api(`/api/conversations`, { method: "DELETE" });
    void pruneLocalSandboxes([]);
    set({ conversations: [], activeId: null, messages: [], activeSystemPrompt: null, activeContextBlock: null, drafts: {} });
  },

  async setActiveTitle(id: string, title: string) {
    await patchConversation(id, { title });
    await get().refreshConversations();
  },

  async setActiveSystemPrompt(prompt: string | null) {
    await patchActive({ activeSystemPrompt: prompt }, { systemPrompt: prompt });
  },

  async setActiveContextBlock(block: string) {
    await patchActive({ activeContextBlock: block }, { contextBlock: block });
  },

  async updateSettings(patch: Partial<AppSettings>) {
    const next = { ...get().settings, ...patch };
    set({ settings: next });
    await api(`/api/settings`, { method: "PUT", body: JSON.stringify(next) });
  },

  setActiveModel(model: string) {
    const next = { ...get().settings, activeModel: model };
    set({ activeModel: model, settings: next });
    void api(`/api/settings`, { method: "PUT", body: JSON.stringify(next) });
  },

  setDraftText(key: string, text: string) {
    const drafts = get().drafts;
    const prev = drafts[key];
    if (!text && (!prev || prev.files.length === 0)) {
      if (!prev) return;
      const next = { ...drafts };
      delete next[key];
      set({ drafts: next });
      return;
    }
    set({ drafts: { ...drafts, [key]: { text, files: prev?.files ?? [] } } });
  },

  setDraftFiles(key: string, files: AttachedFile[]) {
    const drafts = get().drafts;
    const prev = drafts[key];
    if (files.length === 0 && (!prev || !prev.text)) {
      if (!prev) return;
      const next = { ...drafts };
      delete next[key];
      set({ drafts: next });
      return;
    }
    set({ drafts: { ...drafts, [key]: { text: prev?.text ?? "", files } } });
  },

  clearDraft(key: string) {
    const drafts = get().drafts;
    if (!drafts[key]) return;
    const next = { ...drafts };
    delete next[key];
    set({ drafts: next });
  },

  async refreshArtifacts() {
    const artifacts = await api<ArtifactSummary[]>("/api/artifacts");
    set({ artifacts });
  },

  async deleteArtifact(id: string) {
    await api(`/api/artifacts/${id}`, { method: "DELETE" });
    set({ artifacts: get().artifacts.filter((a) => a.id !== id) });
  },

  openArtifactPanel(id: string | null) {
    set({ artifactPanelOpen: true, artifactPanelSelectedId: id, sidebarOpen: false });
  },

  closeArtifactPanel() {
    set({ artifactPanelOpen: false });
  },

  setSidebarOpen(open: boolean) {
    set({ sidebarOpen: open });
  },

  setShowApiKey(show: boolean) {
    set({ showApiKey: show });
  },

  async refreshSkills() {
    const skills = await api<Skill[]>("/api/skills");
    set({ skills });
  },

  async createSkill(input) {
    const skill = await api<Skill>("/api/skills", {
      method: "POST",
      body: JSON.stringify(input),
    });
    set({ skills: [...get().skills, skill].sort((a, b) => a.name.localeCompare(b.name)) });
    return skill;
  },

  async updateSkill(id, patch) {
    const skill = await api<Skill>(`/api/skills/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    set({
      skills: get().skills.map((s) => (s.id === id ? skill : s)).sort((a, b) => a.name.localeCompare(b.name)),
    });
    return skill;
  },

  async deleteSkill(id) {
    await api(`/api/skills/${id}`, { method: "DELETE" });
    const wasPinned = get().activePinnedSkillId === id;
    set({
      skills: get().skills.filter((s) => s.id !== id),
      activePinnedSkillId: wasPinned ? null : get().activePinnedSkillId,
      activeActivatedSkillIds: get().activeActivatedSkillIds.filter((x) => x !== id),
    });
  },

  async setActivePinnedSkill(skillId) {
    await patchActive({ activePinnedSkillId: skillId }, { pinnedSkillId: skillId });
  },

  async setActiveActivatedSkills(skillIds) {
    await patchActive({ activeActivatedSkillIds: skillIds }, { activatedSkillIds: skillIds });
  },

  async setActiveWorkspace(path) {
    const trimmed = path?.trim() || null;
    set({ activeWorkspacePath: trimmed, workspacePrefs: null });
    const { activeId } = get();
    if (activeId) await patchConversation(activeId, { workspacePath: trimmed });
    if (trimmed) {
      try {
        const ws = await api<WorkspacePrefs>("/api/workspaces", {
          method: "POST",
          body: JSON.stringify({ path: trimmed }),
        });
        set({ workspacePrefs: ws });
      } catch {}
    }
  },

  async reloadWorkspacePrefs() {
    const path = get().activeWorkspacePath;
    if (!path) { set({ workspacePrefs: null }); return; }
    try {
      const ws = await api<WorkspacePrefs>("/api/workspaces", {
        method: "POST",
        body: JSON.stringify({ path }),
      });
      set({ workspacePrefs: ws });
    } catch {}
  },

  async saveWorkspacePrefs(patch) {
    const path = get().activeWorkspacePath;
    if (!path) return;
    try {
      const ws = await api<WorkspacePrefs>("/api/workspaces/prefs", {
        method: "POST",
        body: JSON.stringify({ path, ...patch }),
      });
      set({ workspacePrefs: ws });
    } catch {}
  },

  requestApproval(command) {
    return new Promise((resolve) => {
      const id = generateId();
      const path = get().activeWorkspacePath ?? "";
      set({ pendingApproval: { id, command, workspacePath: path, resolve } });
    });
  },

  resolveApproval(decision) {
    const p = get().pendingApproval;
    if (!p) return;
    set({ pendingApproval: null });
    p.resolve(decision);
  },
});
});

export function getActiveSystemPrompt(): string {
  const { activeSystemPrompt, settings } = useStore.getState();
  return activeSystemPrompt ?? settings.defaultSystemPrompt;
}
